/** @jsxImportSource @emotion/react */
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import { css } from '@emotion/react'
import { App } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

dayjs.extend(utc)
dayjs.extend(timezone)

import colors from '@sportninja/common/constants/appColors'
import { PRIMARY_BUTTON_CONTENT } from '@sportninja/common/constants/buttonColors'
import CalendarGrid from './CalendarGrid'
import WeekGrid from './WeekGrid'
import DayGrid from './DayGrid'
import type { CalendarGame } from './CalendarGameCard'
import CalendarGameEditPopover from './CalendarGameEditPopover'
import type { GeneratedGame } from './GeneratedGamesSection'
import type { Venue } from '../hooks/useSchedulerLocations'
import type { DivisionItem } from '../hooks/useSchedulerDivisions'
import type { TimeSlot } from '../hooks/useSchedulerTimeSlots'
import type { SchedulerConstraint } from '../hooks/useSchedulerConstraints'
import type { GameEditData } from '../hooks/useGameEdit'
import {
  buildBlackoutModel,
  facilityNamesById,
  isFullDayBlackout,
  partialBlackoutLabel,
} from '../utils/blackoutScope'

// ─── Division Colors ─────────────────────────────────────────────
const DIVISION_COLORS = [
  '#8B5CF6', // Purple
  '#06B6D4', // Cyan
  '#F97316', // Orange
  '#10B981', // Green
  '#EC4899', // Pink
  '#3B82F6', // Blue
  '#EF4444', // Red
  '#F59E0B', // Amber
]

const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const MONTH_ABBREVS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const DAY_NAMES = [
  'SUNDAY',
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
]

// ─── Styles ──────────────────────────────────────────────────────

const calendarNavStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  background: ${colors.SECONDARY_300};
`

const calendarNavBtnStyles = css`
  background: transparent;
  border: 1px solid ${colors.SECONDARY_100};
  border-radius: 6px;
  color: ${colors.NEUTRAL_50};
  cursor: pointer;
  padding: 4px 10px;
  font-size: 18px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;

  &:hover {
    background: ${colors.SECONDARY_200};
    color: ${colors.WHITE};
  }
`

const calendarMonthTitleStyles = css`
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 16px;
  color: ${colors.WHITE};
  min-width: 180px;
  text-align: center;
`

const gridContainerStyles = css`
  background: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 8px;
  overflow: hidden;
`

const moveModeBannerStyles = css`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 10px 20px;
  background: ${colors.PRIMARY};
  color: ${colors.PRIMARY_TEXT};
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
`

const moveModeCloseStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  color: ${PRIMARY_BUTTON_CONTENT.text};
  background: rgba(0, 0, 0, 0.12);
  border: none;
  border-radius: 4px;
  padding: 4px 12px;
  cursor: pointer;

  &:hover {
    background: rgba(0, 0, 0, 0.2);
  }
`

const todayBtnStyles = css`
  ${calendarNavBtnStyles};
  font-size: 13px;
  padding: 4px 12px;
  margin-left: 8px;
`

// ─── Props ───────────────────────────────────────────────────────

interface ReviewCalendarViewProps {
  viewMode: 'month' | 'week' | 'day'
  games: GeneratedGame[]
  venues: Venue[]
  divisions: DivisionItem[]
  timeSlots: TimeSlot[]
  constraints: SchedulerConstraint[]
  competition: { starts_at?: string; ends_at?: string } | null
  onSaveGame: (gameId: string, data: GameEditData) => Promise<boolean>
  readOnly?: boolean
}

// ─── Component ───────────────────────────────────────────────────

const ReviewCalendarView: React.FC<ReviewCalendarViewProps> = ({
  viewMode,
  games,
  venues,
  divisions,
  timeSlots,
  constraints,
  competition,
  onSaveGame,
  readOnly = false,
}) => {
  const { message } = App.useApp()

  // ─── Earliest game date ──────────────────────────────────────
  const earliestGameDate = useMemo(() => {
    if (games.length === 0) {
      return new Date()
    }
    const dates = games
      .map((g) => {
        if (g.startsAt) {
          return dayjs
            .utc(g.startsAt)
            .tz(g.timezone || 'UTC')
            .toDate()
        }
        return new Date(g.date)
      })
      .filter((d) => !isNaN(d.getTime()))
    if (dates.length === 0) {
      return new Date()
    }
    dates.sort((a, b) => a.getTime() - b.getTime())
    return dates[0]
  }, [games])

  // ─── Calendar navigation state ──────────────────────────────
  const [currentDate, setCurrentDate] = useState<Date | null>(null)
  const calendarDate = currentDate ?? earliestGameDate
  const calendarYear = calendarDate.getFullYear()
  const calendarMonth = calendarDate.getMonth()

  const handlePrevMonth = useCallback(() => {
    setCurrentDate((prev) => {
      const d = prev ?? earliestGameDate
      return new Date(d.getFullYear(), d.getMonth() - 1, 1)
    })
  }, [earliestGameDate])

  const handleNextMonth = useCallback(() => {
    setCurrentDate((prev) => {
      const d = prev ?? earliestGameDate
      return new Date(d.getFullYear(), d.getMonth() + 1, 1)
    })
  }, [earliestGameDate])

  // ─── Week navigation ────────────────────────────────────────
  const weekStart = useMemo(() => {
    const d = new Date(calendarDate)
    d.setDate(d.getDate() - d.getDay())
    d.setHours(0, 0, 0, 0)
    return d
  }, [calendarDate])

  const weekEnd = useMemo(() => {
    const d = new Date(weekStart)
    d.setDate(d.getDate() + 6)
    return d
  }, [weekStart])

  const weekRangeLabel = useMemo(() => {
    const s = weekStart
    const e = weekEnd
    const sMonth = MONTH_ABBREVS[s.getMonth()]
    const eMonth = MONTH_ABBREVS[e.getMonth()]
    const sDay = s.getDate()
    const eDay = e.getDate()
    const eYear = e.getFullYear()
    if (s.getMonth() === e.getMonth()) {
      return `${sMonth} ${sDay} – ${eDay}, ${eYear}`
    }
    return `${sMonth} ${sDay} – ${eMonth} ${eDay}, ${eYear}`
  }, [weekStart, weekEnd])

  const handlePrevWeek = useCallback(() => {
    setCurrentDate((prev) => {
      const d = prev ?? earliestGameDate
      return new Date(d.getFullYear(), d.getMonth(), d.getDate() - 7)
    })
  }, [earliestGameDate])

  const handleNextWeek = useCallback(() => {
    setCurrentDate((prev) => {
      const d = prev ?? earliestGameDate
      return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7)
    })
  }, [earliestGameDate])

  // ─── Day navigation ─────────────────────────────────────────
  const dayLabel = useMemo(() => {
    const d = calendarDate
    const monthAbbrev = MONTH_ABBREVS[d.getMonth()].toUpperCase()
    return `${
      DAY_NAMES[d.getDay()]
    }, ${d.getDate()} ${monthAbbrev} ${d.getFullYear()}`
  }, [calendarDate])

  const handlePrevDay = useCallback(() => {
    setCurrentDate((prev) => {
      const d = prev ?? earliestGameDate
      return new Date(d.getFullYear(), d.getMonth(), d.getDate() - 1)
    })
  }, [earliestGameDate])

  const handleNextDay = useCallback(() => {
    setCurrentDate((prev) => {
      const d = prev ?? earliestGameDate
      return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1)
    })
  }, [earliestGameDate])

  // ─── Division color map ─────────────────────────────────────
  const divisionColorMap = useMemo(() => {
    const map = new Map<string, string>()
    const uniqueIds: string[] = []
    for (const g of games) {
      if (g.divisionId && !map.has(g.divisionId)) {
        map.set(
          g.divisionId,
          DIVISION_COLORS[uniqueIds.length % DIVISION_COLORS.length]
        )
        uniqueIds.push(g.divisionId)
      }
    }
    return map
  }, [games])

  // ─── Calendar games transform ───────────────────────────────
  const calendarGames: CalendarGame[] = useMemo(() => {
    return games.map((game) => ({
      id: game.id,
      date: game.startsAt
        ? dayjs
            .utc(game.startsAt)
            .tz(game.timezone || 'UTC')
            .toDate()
        : new Date(game.date),
      time: game.time,
      homeTeam: game.homeTeam,
      awayTeam: game.awayTeam,
      venue: game.location,
      divisionColor:
        divisionColorMap.get(game.divisionId) ?? DIVISION_COLORS[0],
      homeTeamId: game.homeTeamId ?? '',
      awayTeamId: game.awayTeamId ?? '',
      locationId: game.locationId ?? '',
      surfaceId: game.surfaceId ?? '',
      divisionId: game.divisionId,
      startsAt: game.startsAt ?? '',
      endsAt: game.endsAt ?? '',
      timezone: game.timezone,
      gameNumber: game.gameNumber,
    }))
  }, [games, divisionColorMap])

  // ─── Blackout dates ─────────────────────────────────────────
  // Facility-AWARE blackouts. Reading only `params.dates` (the old behaviour)
  // threw the facility/venue scope away, so a blackout scoped to ONE rink blacked
  // out the whole day: the calendar hid every game on it and refused a drop,
  // while the table view showed the games all along (flight 164, Jan 3 —
  // Primary out, three real games on Secondary).
  //
  // `blackoutDates` now means FULL-day only: a global blackout, or every facility
  // out. Days where only some ice is out are PARTIAL — the games render, with a
  // chip naming what is unavailable.
  const blackoutModel = useMemo(
    () => buildBlackoutModel(constraints, venues),
    [constraints, venues]
  )

  const allFacilityIds = useMemo(
    () => venues.flatMap((v) => (v.facilities ?? []).map((f) => f.id)),
    [venues]
  )

  const facilityNames = useMemo(() => facilityNamesById(venues), [venues])

  const calendarBlackoutDates = useMemo(() => {
    const dates: Date[] = []
    for (const dateKey of blackoutModel.keys()) {
      if (isFullDayBlackout(blackoutModel, dateKey, allFacilityIds)) {
        dates.push(new Date(dateKey + 'T00:00:00'))
      }
    }
    return dates
  }, [blackoutModel, allFacilityIds])

  const partialBlackoutLabels = useMemo(() => {
    const out = new Map<string, string>()
    for (const dateKey of blackoutModel.keys()) {
      const label = partialBlackoutLabel(
        blackoutModel,
        dateKey,
        allFacilityIds,
        facilityNames
      )
      if (label) {
        out.set(dateKey, label)
      }
    }
    return out
  }, [blackoutModel, allFacilityIds, facilityNames])

  const calendarBlackoutDateStrings = useMemo(() => {
    const set = new Set<string>()
    for (const d of calendarBlackoutDates) {
      set.add(d.toISOString().slice(0, 10))
    }
    return set
  }, [calendarBlackoutDates])

  // ─── Competition date boundaries ────────────────────────────
  const minDate = useMemo(
    () =>
      competition?.starts_at
        ? dayjs.utc(competition.starts_at).format('YYYY-MM-DD')
        : undefined,
    [competition?.starts_at]
  )

  const maxDate = useMemo(
    () =>
      competition?.ends_at
        ? dayjs.utc(competition.ends_at).format('YYYY-MM-DD')
        : undefined,
    [competition?.ends_at]
  )

  // ─── Popover state ──────────────────────────────────────────
  const [popoverGameId, setPopoverGameId] = useState<string | null>(null)
  const popoverSnapshotRef = useRef<{
    locationId: string
    surfaceId: string
    homeTeamId: string
    awayTeamId: string
    startsAt: string
    endsAt: string
  } | null>(null)

  const popoverGame = useMemo(
    () =>
      popoverGameId
        ? calendarGames.find((g) => g.id === popoverGameId) ?? null
        : null,
    [popoverGameId, calendarGames]
  )

  const handleCalendarGameClick = useCallback((game: CalendarGame) => {
    popoverSnapshotRef.current = {
      locationId: game.locationId,
      surfaceId: game.surfaceId,
      homeTeamId: game.homeTeamId,
      awayTeamId: game.awayTeamId,
      startsAt: game.startsAt,
      endsAt: game.endsAt,
    }
    setPopoverGameId(game.id)
  }, [])

  const handlePopoverClose = useCallback(() => {
    setPopoverGameId(null)
    popoverSnapshotRef.current = null
  }, [])

  const handlePopoverSave = useCallback(
    async (gameId: string, data: Record<string, unknown>): Promise<boolean> => {
      setPopoverGameId(null)
      return onSaveGame(gameId, data as GameEditData)
    },
    [onSaveGame]
  )

  // ─── Move mode state ───────────────────────────────────────
  const [moveMode, setMoveMode] = useState<{
    gameId: string
    game: GeneratedGame
  } | null>(null)

  useEffect(() => {
    if (!moveMode) {
      return
    }
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMoveMode(null)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [moveMode])

  const handleMoveModeClick = useCallback(
    (targetDate: Date) => {
      if (!moveMode) {
        return
      }

      const tY = targetDate.getFullYear()
      const tM = String(targetDate.getMonth() + 1).padStart(2, '0')
      const tD = String(targetDate.getDate()).padStart(2, '0')
      const targetDateStr = `${tY}-${tM}-${tD}`

      onSaveGame(moveMode.gameId, {
        target_date: targetDateStr,
      } as unknown as GameEditData)
      setMoveMode(null)
    },
    [moveMode, onSaveGame]
  )

  const handleEnterMoveMode = useCallback(
    (gameId: string) => {
      const game = games.find((g) => g.id === gameId)
      if (!game) {
        return
      }
      setPopoverGameId(null)
      setMoveMode({ gameId, game })
    },
    [games]
  )

  const handleExitMoveMode = useCallback(() => {
    setMoveMode(null)
  }, [])

  const popoverContent = useMemo(() => {
    if (!popoverGame) {
      return null
    }
    return (
      <CalendarGameEditPopover
        game={popoverGame}
        venues={venues}
        divisions={divisions}
        timeSlots={timeSlots}
        onClose={handlePopoverClose}
        onSave={handlePopoverSave}
        onMoveToDate={readOnly ? undefined : handleEnterMoveMode}
        readOnly={readOnly}
      />
    )
  }, [
    popoverGame,
    venues,
    divisions,
    timeSlots,
    handlePopoverClose,
    handlePopoverSave,
    handleEnterMoveMode,
    readOnly,
  ])

  // ─── Drag-and-drop handler ─────────────────────────────────
  const handleCalendarDragEnd = useCallback(
    (gameId: string, targetDate: Date) => {
      if (readOnly) {
        return
      }
      const game = games.find((g) => g.id === gameId)
      if (!game) {
        return
      }
      const tY = targetDate.getFullYear()
      const tM = String(targetDate.getMonth() + 1).padStart(2, '0')
      const tD = String(targetDate.getDate()).padStart(2, '0')
      const targetDateStr = `${tY}-${tM}-${tD}`

      if (calendarBlackoutDateStrings.has(targetDateStr)) {
        message.warning('This date is blacked out')
        return
      }
      if (minDate && targetDateStr < minDate) {
        message.warning('This date is before the competition start date')
        return
      }
      if (maxDate && targetDateStr > maxDate) {
        message.warning('This date is after the competition end date')
        return
      }

      onSaveGame(gameId, {
        target_date: targetDateStr,
      } as unknown as GameEditData)
    },
    [
      readOnly,
      games,
      onSaveGame,
      calendarBlackoutDateStrings,
      message,
      minDate,
      maxDate,
    ]
  )

  // ─── Render ────────────────────────────────────────────────
  return (
    <>
      {viewMode === 'month' && (
        <div css={gridContainerStyles}>
          <div css={calendarNavStyles}>
            <button
              type='button'
              css={calendarNavBtnStyles}
              onClick={handlePrevMonth}
              aria-label='Previous month'
            >
              &#8249;
            </button>
            <span css={calendarMonthTitleStyles}>
              {MONTH_NAMES[calendarMonth]} {calendarYear}
            </span>
            <button
              type='button'
              css={calendarNavBtnStyles}
              onClick={handleNextMonth}
              aria-label='Next month'
            >
              &#8250;
            </button>
            <button
              type='button'
              css={todayBtnStyles}
              onClick={() => setCurrentDate(new Date())}
              aria-label='Go to today'
            >
              Today
            </button>
          </div>
          <div
            css={css`
              padding: 0 16px 16px;
              background: ${colors.SECONDARY_400};
            `}
          >
            <CalendarGrid
              year={calendarYear}
              month={calendarMonth}
              games={calendarGames}
              blackoutDates={calendarBlackoutDates}
              partialBlackoutLabels={partialBlackoutLabels}
              onGameClick={handleCalendarGameClick}
              popoverGameId={popoverGameId}
              popoverContent={popoverContent}
              onPopoverOpenChange={setPopoverGameId}
              onDragEnd={readOnly ? undefined : handleCalendarDragEnd}
              onDayClick={moveMode ? handleMoveModeClick : undefined}
              timeSlots={timeSlots}
              competitionStartDate={minDate}
              competitionEndDate={maxDate}
              constraints={constraints}
            />
          </div>
        </div>
      )}

      {viewMode === 'week' && (
        <div css={gridContainerStyles}>
          <div css={calendarNavStyles}>
            <button
              type='button'
              css={calendarNavBtnStyles}
              onClick={handlePrevWeek}
              aria-label='Previous week'
            >
              &#8249;
            </button>
            <span css={calendarMonthTitleStyles}>{weekRangeLabel}</span>
            <button
              type='button'
              css={calendarNavBtnStyles}
              onClick={handleNextWeek}
              aria-label='Next week'
            >
              &#8250;
            </button>
            <button
              type='button'
              css={todayBtnStyles}
              onClick={() => setCurrentDate(new Date())}
              aria-label='Go to today'
            >
              Today
            </button>
          </div>
          <div
            css={css`
              padding: 0 16px 16px;
              background: ${colors.SECONDARY_400};
            `}
          >
            <WeekGrid
              weekStart={weekStart}
              games={calendarGames}
              blackoutDates={calendarBlackoutDates}
              partialBlackoutLabels={partialBlackoutLabels}
              onGameClick={handleCalendarGameClick}
              popoverGameId={popoverGameId}
              popoverContent={popoverContent}
              onPopoverOpenChange={setPopoverGameId}
              onDragEnd={readOnly ? undefined : handleCalendarDragEnd}
              timeSlots={timeSlots}
              competitionStartDate={minDate}
              competitionEndDate={maxDate}
              constraints={constraints}
            />
          </div>
        </div>
      )}

      {viewMode === 'day' && (
        <div css={gridContainerStyles}>
          <div css={calendarNavStyles}>
            <button
              type='button'
              css={calendarNavBtnStyles}
              onClick={handlePrevDay}
              aria-label='Previous day'
            >
              &#8249;
            </button>
            <span css={calendarMonthTitleStyles}>{dayLabel}</span>
            <button
              type='button'
              css={calendarNavBtnStyles}
              onClick={handleNextDay}
              aria-label='Next day'
            >
              &#8250;
            </button>
            <button
              type='button'
              css={todayBtnStyles}
              onClick={() => setCurrentDate(new Date())}
              aria-label='Go to today'
            >
              Today
            </button>
          </div>
          <div
            css={css`
              padding: 0 16px 16px;
              background: ${colors.SECONDARY_400};
              overflow-x: auto;
            `}
          >
            <DayGrid
              selectedDate={calendarDate}
              games={calendarGames}
              venues={venues}
              onGameClick={handleCalendarGameClick}
              popoverGameId={popoverGameId}
              popoverContent={popoverContent}
              onPopoverOpenChange={setPopoverGameId}
            />
          </div>
        </div>
      )}

      {/* Move mode banner */}
      {moveMode && (
        <div css={moveModeBannerStyles}>
          <span>
            Click a target date to move Game #{moveMode.game.gameNumber}
          </span>
          <button
            type='button'
            onClick={handleExitMoveMode}
            css={moveModeCloseStyles}
          >
            Cancel
          </button>
        </div>
      )}
    </>
  )
}

export default ReviewCalendarView
